import { Box } from '@mui/material';
import { SocketClientMessage } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { useDispatch } from 'react-redux';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useEntityRepositoryStatus, useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { LolTournamentEntity, TournamentEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import { TournamentBracketStoreStatus } from 'modules/bracket/store/enum';
import { getTournamentBracket } from 'modules/bracket/store/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Loader } from 'shared/components/Loader';
import { BracketControlsProvider } from '../providers';
import { UpdateReadyUsersPayload } from '../store/interfaces';
import { tournamentBracketReducers } from '../store/slice';
import { useStyles } from './Bracket.styles';
import { BracketControls } from './BracketControls';
import { DoubleEliminationBracket } from './DoubleEliminationBracket';
import { SingleEliminationBracket } from './SingleEliminationBracket';

const { Hiding } = TournamentBracketStoreStatus;

interface BracketProps {
  isSubscriptionsEnabled?: boolean;
}

export const Bracket: FC<BracketProps> = ({ isSubscriptionsEnabled = false }) => {
  const hiding = useEntityRepositoryStatus({ [EntityStoreName.TournamentBracket]: Hiding });
  const tournament = useSelector(currentTournamentSelector.tournament) as LolTournamentEntity;
  const isDoubleEliminationTournament = useSelector(
    currentTournamentSelector.isDoubleEliminationTournament
  );
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const actions = useMemo(() => {
    if (!isSubscriptionsEnabled || !tournament?.id) {
      return [];
    }

    return [getTournamentBracket(tournament?.id as string)];
  }, [tournament?.id, isSubscriptionsEnabled]);
  const { fetch, loading } = useFetchData<TournamentEntity>(actions, { fetchOnInit: true });

  const updateTournamentMatchGameHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== tournament?.id || !isSubscriptionsEnabled) {
        return;
      }

      fetch(false);
    },
    [fetch, isSubscriptionsEnabled, tournament?.id]
  );

  const updateBracketHandler = useCallback(
    (data: UpdateReadyUsersPayload) => {
      if (!isSubscriptionsEnabled) {
        return;
      }

      dispatch(tournamentBracketReducers.updateBracketGame(data));
    },
    [dispatch, isSubscriptionsEnabled]
  );

  const matchGameSubscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.TournamentMatchGame,
        handler: updateTournamentMatchGameHandler,
      },
    ],
    [updateTournamentMatchGameHandler]
  );

  const bracketSubscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.TournamentMatchGameUsersStateUpdated,
        handler: updateBracketHandler,
      },
    ],
    [updateBracketHandler]
  );

  useSocketSubscriber(matchGameSubscriptions);
  useSocketSubscriber(bracketSubscriptions);

  if (loading || hiding) {
    return <Loader />;
  }

  return (
    <BracketControlsProvider>
      <BracketControls isFullscreenMode={!isSubscriptionsEnabled} />

      <Box className={classNames(classes.root)}>
        {isDoubleEliminationTournament ? (
          <DoubleEliminationBracket />
        ) : (
          <SingleEliminationBracket />
        )}
      </Box>
    </BracketControlsProvider>
  );
};
